import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styles from './index.module.css';
import JournalEditor from './JournalEditor';
import SectionsPane from './SectionsPane';
import EntriesPane from './EntriesPane';
import EditorChrome, { DocumentHeader } from './EditorChrome';
import ShareSectionModal from './ShareSectionModal';
import RewriteAIModal from './RewriteAIModal';
import MoveEntryModal from './MoveEntryModal';
import { useDispatch, useSelector } from 'react-redux';
import { GetNotes, PostNotesPage, DeleteNotePage, PostNote, DeleteNote, PatchNote, PatchNotesPage } from '../../redux/dashboardSlice';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../Observations';
import TaskFormSidebar from '../TaskManagement/TaskFormSidebar';
import { PostInformationRequestSidebar } from '../IRL';
import { isAskLensOutputSection } from '../../utils/askLensJournal';
import { getShortDisplayName, mergeNoteContent, noteHasContent, resolveEntryTitle, splitLongPlainTitle, splitNoteContent } from './journalUtils';
import { exportNoteAsDocx, exportNoteAsPdf } from './exportNote';
import { buildExportPdfFilename } from '../../utils/exportPdfFilename';

const Journal = () => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state.dashboard?.notes || []);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const selectedProject = useSelector(state => state.projectData?.selectedProject);
    const [selectedSectionId, setSelectedSectionId] = useState(null);
    const [isAddingEntry, setIsAddingEntry] = useState(false);

    const [initialEditorData, setInitialEditorData] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [selectedSectionForShare, setSelectedSectionForShare] = useState(null);
    const [isRewriteAIModalOpen, setIsRewriteAIModalOpen] = useState(false);
    const [selectedNoteForRewrite, setSelectedNoteForRewrite] = useState(null);
    const [aiMode, setAiMode] = useState('rewrite');
    const [isMoveEntryModalOpen, setIsMoveEntryModalOpen] = useState(false);
    const [expandedNoteId, setExpandedNoteId] = useState(null);
    const [editingNote, setEditingNote] = useState(null);
    const [draftContent, setDraftContent] = useState(null);
    const [entryTitle, setEntryTitle] = useState('');
    const debounceTimeoutRef = useRef(null);
    const editorApiRef = React.useRef(null);
    const [autoSavedNoteId, setAutoSavedNoteId] = useState(null);
    const [editorLoadKey, setEditorLoadKey] = useState(0);
    const isCreatingNoteRef = useRef(false);

    const latestDraftContentRef = useRef(null);
    const isAddingEntryRef = useRef(false);
    const selectedSectionIdRef = useRef(null);
    const editingNoteRef = useRef(null);
    const autoSavedNoteIdRef = useRef(null);
    const entryTitleRef = useRef('');
    const sectionMenuDeleteRef = useRef(null);
    const entryMenuDeleteRef = useRef(null);
    const isInitializingEditorRef = useRef(false);
    const originalNoteContentRef = useRef(null);
    const saveGenerationRef = useRef(0);
    const creatingPromiseRef = useRef(null);
    const localNoteContentRef = useRef({});
    const initGenerationRef = useRef(0);
    const [selectedSectionForDelete, setSelectedSectionForDelete] = useState(null);
    const [selectedEntryForDelete, setSelectedEntryForDelete] = useState(null);
    const sectionMakePrivateRef = useRef(null);
    const [selectedSectionForMakePrivate, setSelectedSectionForMakePrivate] = useState(null);
    const [editingSectionName, setEditingSectionName] = useState(null);
    const [editingSectionValue, setEditingSectionValue] = useState('');
    const [isSectionsCollapsed, setIsSectionsCollapsed] = useState(false);
    const [isEditorMaximized, setIsEditorMaximized] = useState(false);
    const [sortOrder, setSortOrder] = useState('desc');
    const [saveStatus, setSaveStatus] = useState('saved');

    const [showObsSidebar, setShowObsSidebar] = React.useState(false);
    const [showTaskSidebar, setShowTaskSidebar] = React.useState(false);
    const [showIRLSidebar, setShowIRLSidebar] = React.useState(false);
    const [rewrittenContentForSidebar, setRewrittenContentForSidebar] = React.useState(null);

    const getNoteHtml = note => note?.notes || '';

    const applyTitleAndBody = useCallback((rawTitle, rawBodyHtml) => {
        const { title, overflow } = splitLongPlainTitle(rawTitle || '');
        const overflowHtml = overflow ? `<p>${overflow.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : '';
        const bodyHtml = `${overflowHtml}${rawBodyHtml || ''}`;
        return { title, bodyHtml };
    }, []);

    const htmlToPlainText = html => {
        const container = document.createElement('div');
        container.innerHTML = html || '';
        return container.textContent || container.innerText || '';
    };

    const stripHtmlText = html => (html || '').replace(/<[^>]*>/g, '').trim();

    const isSameNoteText = (a, b) => stripHtmlText(a) === stripHtmlText(b);

    const beginEditorLoad = () => {
        const gen = ++initGenerationRef.current;
        isInitializingEditorRef.current = true;
        window.setTimeout(() => {
            if (gen === initGenerationRef.current) {
                isInitializingEditorRef.current = false;
            }
        }, 150);
    };

    useEffect(() => {
        dispatch(GetNotes());
    }, [dispatch]);

    useEffect(() => {
        if (!selectedSectionId && notesState && notesState.length > 0) {
            setSelectedSectionId(notesState[0].id);
        }
    }, [notesState, selectedSectionId]);

    const sections = useMemo(() => {
        return (notesState || []).map(n => {
            let tag = null;
            if (n?.is_project_default) {
                tag = { text: 'Public', color: '#DA0C81' };
            } else if (n?.is_shared) {
                tag = { text: n?.owner_name, color: '#192BC2' };
            } else if (n?.shared_with && n.shared_with.length > 0) {
                tag = { text: 'Shared', color: '#192BC2' };
            }

            return {
                id: n.id,
                name: n.name,
                tag,
                isActive: n.id === selectedSectionId,
                shared_with: n.shared_with,
                created_by: n.created_by,
                is_project_default: n.is_project_default,
                is_ask_lens_output: isAskLensOutputSection(n),
                shared_with_names: n.shared_with_names || [],
                owner_name: n.owner_name,
                is_shared: n.is_shared,
                notes: n.notes,
            };
        });
    }, [notesState, selectedSectionId]);

    const selectedSection = useMemo(() => (notesState || []).find(s => s.id === selectedSectionId) || null, [notesState, selectedSectionId]);

    const sortedNotes = useMemo(() => {
        const notes = selectedSection?.notes || [];
        return [...notes].sort((a, b) => {
            const dateA = new Date(a.created);
            const dateB = new Date(b.created);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    }, [selectedSection?.notes, sortOrder]);

    const entryOwnerName = useMemo(() => {
        const ownerId = editingNote?.created_by || user?.id;
        const fullName = projectUsersAll?.find(item => item.value.user_id === ownerId)?.value?.full_name;
        return getShortDisplayName(fullName) || (user?.full_name ? getShortDisplayName(user.full_name) : '—');
    }, [editingNote, projectUsersAll, user]);

    const breadcrumbEntryTitle = useMemo(() => {
        const trimmed = (entryTitle || '').trim();
        if (trimmed) return trimmed;

        if (editingNote) {
            const fromNote = resolveEntryTitle('', getNoteHtml(editingNote));
            if (fromNote) return fromNote;
        }

        return 'Untitled';
    }, [editingNote, entryTitle]);

    const resetEditorState = useCallback(() => {
        beginEditorLoad();
        setExpandedNoteId(null);
        setEditingNote(null);
        setInitialEditorData(null);
        setDraftContent(null);
        latestDraftContentRef.current = null;
        setEntryTitle('');
        entryTitleRef.current = '';
        setAutoSavedNoteId(null);
        isCreatingNoteRef.current = false;
        originalNoteContentRef.current = null;
        setSaveStatus('saved');
        setEditorLoadKey(k => k + 1);
        if (editorApiRef.current?.clear) {
            editorApiRef.current.clear();
        }
    }, []);

    const persistCurrentNote = useCallback(
        async generation => {
            if (!isAddingEntryRef.current || !selectedSectionIdRef.current) return;

            const noteId = editingNoteRef.current?.id || autoSavedNoteIdRef.current;
            const sectionId = selectedSectionIdRef.current;
            const title = entryTitleRef.current;

            let bodyHtml = latestDraftContentRef.current || '';
            try {
                const live = await editorApiRef.current?.save?.();
                if (typeof live === 'string' && (stripHtmlText(live) || !stripHtmlText(bodyHtml))) {
                    bodyHtml = live;
                }
            } catch {
                // fall back to last known draft
            }

            if (generation !== saveGenerationRef.current) return;
            if (!noteHasContent(title, bodyHtml)) return;

            const html = mergeNoteContent(title, bodyHtml);
            if (originalNoteContentRef.current && isSameNoteText(originalNoteContentRef.current, html)) {
                setSaveStatus('saved');
                return;
            }

            if (noteId) {
                localNoteContentRef.current[noteId] = html;
            }

            try {
                setSaveStatus('saving');
                const payload = {
                    notepad: sectionId,
                    notes: html,
                };

                if (noteId) {
                    await dispatch(PatchNote({ id: noteId, ...payload }));
                    if (generation !== saveGenerationRef.current) return;
                    originalNoteContentRef.current = html;
                    setSaveStatus('saved');
                    return;
                }

                if (creatingPromiseRef.current) {
                    const inFlight = await creatingPromiseRef.current;
                    const createdId = inFlight?.payload?.data?.id;
                    if (generation !== saveGenerationRef.current) {
                        if (createdId) localNoteContentRef.current[createdId] = html;
                        return;
                    }
                    if (createdId) {
                        setAutoSavedNoteId(createdId);
                        await dispatch(PatchNote({ id: createdId, notepad: sectionId, notes: html }));
                        localNoteContentRef.current[createdId] = html;
                        originalNoteContentRef.current = html;
                    }
                    setSaveStatus('saved');
                    return;
                }

                isCreatingNoteRef.current = true;
                const promise = dispatch(PostNote(payload));
                creatingPromiseRef.current = promise;
                const result = await promise;
                creatingPromiseRef.current = null;
                isCreatingNoteRef.current = false;

                const createdId = result?.payload?.data?.id;
                if (createdId) {
                    localNoteContentRef.current[createdId] = html;
                }

                if (generation !== saveGenerationRef.current) return;

                if (createdId) {
                    setAutoSavedNoteId(createdId);
                    originalNoteContentRef.current = html;
                }
                setSaveStatus('saved');
            } catch (e) {
                console.error('Auto-save failed:', e);
                isCreatingNoteRef.current = false;
                creatingPromiseRef.current = null;
                if (generation === saveGenerationRef.current) {
                    setSaveStatus('unsaved');
                }
            }
        },
        [dispatch],
    );

    const scheduleSave = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            debounceTimeoutRef.current = null;
            persistCurrentNote(saveGenerationRef.current);
        }, 1000);
    }, [persistCurrentNote]);

    const flushPendingSave = useCallback(async () => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }
        const generation = ++saveGenerationRef.current;
        await persistCurrentNote(generation);
    }, [persistCurrentNote]);

    const handleTitleChange = useCallback(
        title => {
            const next = (title || '').slice(0, 45);
            setEntryTitle(next);
            entryTitleRef.current = next;
            setSaveStatus('unsaved');
            scheduleSave();
        },
        [scheduleSave],
    );

    const handleTitlePaste = useCallback(
        async (title, overflowBodyHtml) => {
            const nextTitle = (title || '').slice(0, 45);
            setEntryTitle(nextTitle);
            entryTitleRef.current = nextTitle;
            setSaveStatus('unsaved');

            if (overflowBodyHtml?.trim()) {
                const existingBody = (await editorApiRef.current?.save?.()) || draftContent || '';
                const existingText = existingBody.replace(/<[^>]*>/g, '').trim();
                const mergedBody = existingText ? `${overflowBodyHtml}${existingBody}` : overflowBodyHtml;

                beginEditorLoad();
                setInitialEditorData(mergedBody);
                setDraftContent(mergedBody);
                latestDraftContentRef.current = mergedBody;
                setEditorLoadKey(k => k + 1);

                setTimeout(() => {
                    editorApiRef.current?.focus?.('');
                }, 300);
            }

            scheduleSave();
        },
        [draftContent, scheduleSave],
    );

    const handleMoveToBody = useCallback((extraText = '') => {
        editorApiRef.current?.focus?.(extraText);
    }, []);

    useEffect(() => {
        entryTitleRef.current = entryTitle;
    }, [entryTitle]);

    useEffect(() => {
        latestDraftContentRef.current = draftContent;
    }, [draftContent]);

    useEffect(() => {
        isAddingEntryRef.current = isAddingEntry;
    }, [isAddingEntry]);

    useEffect(() => {
        selectedSectionIdRef.current = selectedSectionId;
    }, [selectedSectionId]);

    useEffect(() => {
        editingNoteRef.current = editingNote;
    }, [editingNote]);

    useEffect(() => {
        if (autoSavedNoteId && !editingNote && selectedSectionId) {
            const section = notesState.find(s => s.id === selectedSectionId);
            const newNote = section?.notes?.find(n => n.id === autoSavedNoteId);
            if (newNote) {
                setEditingNote(newNote);
                const noteHtml = localNoteContentRef.current[newNote.id] || getNoteHtml(newNote);
                if (!originalNoteContentRef.current) {
                    originalNoteContentRef.current = noteHtml;
                }
            }
        }
    }, [autoSavedNoteId, notesState, selectedSectionId, editingNote]);

    useEffect(() => {
        autoSavedNoteIdRef.current = autoSavedNoteId;
    }, [autoSavedNoteId]);

    useEffect(() => {
        return () => {
            if (isAddingEntryRef.current) {
                persistCurrentNote(saveGenerationRef.current);
            }
        };
        // Persist only on unmount — toggling isAddingEntry used to save after the editor was already cleared.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditorChange = useCallback(
        html => {
            if (isInitializingEditorRef.current) {
                return;
            }

            latestDraftContentRef.current = html;
            setDraftContent(html);

            const merged = mergeNoteContent(entryTitleRef.current, html);
            if (originalNoteContentRef.current && isSameNoteText(originalNoteContentRef.current, merged)) {
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                    debounceTimeoutRef.current = null;
                }
                setSaveStatus('saved');
                return;
            }

            setSaveStatus('unsaved');
            scheduleSave();
        },
        [scheduleSave],
    );

    const handleDeleteSection = sectionId => {
        dispatch(DeleteNotePage(sectionId));
        setSelectedSectionForDelete(null);
    };

    const handleDeleteEntry = noteId => {
        dispatch(DeleteNote({ id: noteId, notepad: selectedSectionId }));
        setSelectedEntryForDelete(null);
        setIsAddingEntry(false);
        resetEditorState();
    };

    const handleSectionDeleteClick = section => {
        setSelectedSectionForDelete(section);
        sectionMenuDeleteRef.current?.click();
    };

    const handleEntryDeleteClick = entry => {
        const noteToDelete = entry || editingNote;
        if (!noteToDelete) return;
        setSelectedEntryForDelete(noteToDelete);
        entryMenuDeleteRef.current?.click();
    };

    const handleRenameSection = (sectionId, currentName) => {
        setEditingSectionName(sectionId);
        setEditingSectionValue(currentName);
    };

    const handleSectionNameChange = e => {
        setEditingSectionValue(e.target.value);
    };

    const handleSaveSectionName = async () => {
        if (editingSectionName && editingSectionValue.trim()) {
            try {
                await dispatch(
                    PatchNotesPage({
                        id: editingSectionName,
                        name: editingSectionValue.trim(),
                    }),
                ).unwrap();
                setEditingSectionName(null);
                setEditingSectionValue('');
            } catch (error) {
                console.error('Failed to update section name:', error);
            }
        }
    };

    const handleMakePrivate = async section => {
        try {
            await dispatch(
                PatchNotesPage({
                    id: section.id,
                    shared_with: [],
                }),
            ).unwrap();
        } catch (error) {
            console.error('Failed to make section private:', error);
        }
    };

    const handleMakePrivateClick = section => {
        setSelectedSectionForMakePrivate(section);
        sectionMakePrivateRef.current?.click();
    };

    const handleRewriteWithAI = async (mode = 'rewrite', note = null) => {
        let targetNote = isAddingEntry ? editingNote || note : note;

        if (isAddingEntry) {
            let latestHtml = '';
            if (editorApiRef.current) {
                try {
                    latestHtml = (await editorApiRef.current?.save?.()) || '';
                } catch (error) {
                    console.error('Error getting editor content:', error);
                }
            }
            if (!latestHtml && draftContent) {
                latestHtml = draftContent;
            }
            latestHtml = mergeNoteContent(entryTitleRef.current, latestHtml);
            if (latestHtml) {
                if (targetNote) {
                    targetNote = { ...targetNote, notes: latestHtml };
                } else {
                    targetNote = { notes: latestHtml };
                }
            }
        }

        if (!targetNote) return;
        setSelectedNoteForRewrite(targetNote);
        setAiMode(mode);
        setIsRewriteAIModalOpen(true);
    };

    const loadNoteIntoEditor = useCallback(
        note => {
            beginEditorLoad();
            const noteHtml = (note?.id && localNoteContentRef.current[note.id]) || getNoteHtml(note);
            const { title: rawTitle, bodyHtml: rawBodyHtml } = splitNoteContent(noteHtml);
            const { title, bodyHtml } = applyTitleAndBody(rawTitle, rawBodyHtml);
            originalNoteContentRef.current = noteHtml;
            setEntryTitle(title);
            entryTitleRef.current = title;
            setInitialEditorData(bodyHtml);
            setDraftContent(bodyHtml);
            latestDraftContentRef.current = bodyHtml;
            setEditorLoadKey(k => k + 1);
            setSaveStatus('saved');
        },
        [applyTitleAndBody],
    );

    const handleSectionSelect = async section => {
        await flushPendingSave();
        if (expandedNoteId) setExpandedNoteId(null);
        setEditingNote(null);
        if (isAddingEntryRef.current) {
            setIsAddingEntry(false);
            resetEditorState();
        }
        setSelectedSectionId(section.id);
        setIsEditorMaximized(false);
    };

    const handleAddSection = () => {
        dispatch(PostNotesPage({ name: 'New Section', project: localStorage['project_id'] }))
            .unwrap()
            .then(res => {
                if (res?.data?.id) {
                    setSelectedSectionId(res.data.id);
                    setEditingSectionName(res.data.id);
                    setEditingSectionValue('New Section');
                }
            })
            .catch(() => {});
    };

    const handleAddEntry = async () => {
        if (!selectedSectionId) {
            toast.warning('Please select a section first');
            return;
        }
        await flushPendingSave();
        resetEditorState();
        setIsAddingEntry(true);
        setSaveStatus('unsaved');
    };

    const handleSelectEntry = async note => {
        const currentId = editingNoteRef.current?.id || autoSavedNoteIdRef.current;
        if (currentId && note?.id === currentId && isAddingEntryRef.current) {
            return;
        }

        await flushPendingSave();
        setExpandedNoteId(null);
        setEditingNote(note);
        setAutoSavedNoteId(null);
        loadNoteIntoEditor(note);
        setIsAddingEntry(true);
    };

    const handleShareFromEditor = () => {
        if (!selectedSection) return;
        setSelectedSectionForShare(selectedSection);
        setIsShareModalOpen(true);
    };

    const handleExportNote = useCallback(
        async format => {
            const title = (entryTitleRef.current || entryTitle || 'Untitled').trim() || 'Untitled';
            let bodyHtml = '';
            try {
                bodyHtml = (await editorApiRef.current?.save?.()) || draftContent || initialEditorData || '';
            } catch (error) {
                console.error('Failed to read editor content for export:', error);
                bodyHtml = draftContent || initialEditorData || '';
            }

            if (!noteHasContent(title, bodyHtml)) {
                toast.warning('Nothing to export yet');
                return;
            }

            if (format === 'pdf') {
                await exportNoteAsPdf({
                    title,
                    bodyHtml,
                    filename: buildExportPdfFilename({ project: selectedProject, label: 'Journal' }),
                });
                toast.success('Exported as PDF');
                return;
            }

            if (format === 'docx') {
                await exportNoteAsDocx({ title, bodyHtml });
                toast.success('Exported as DOCX');
            }
        },
        [draftContent, entryTitle, initialEditorData, selectedProject],
    );

    const handleToggleMaximize = () => {
        setIsEditorMaximized(prev => {
            if (prev) {
                setIsSectionsCollapsed(false);
                return false;
            }
            setIsSectionsCollapsed(true);
            return true;
        });
    };

    const showEntriesPane = !isEditorMaximized;
    const showSectionsPane = !isSectionsCollapsed && !isEditorMaximized;

    return (
        <div className={styles.journalContainer}>
            <div className={styles.journalLayout}>
                {showSectionsPane && (
                    <SectionsPane
                        sections={sections}
                        notesState={notesState}
                        user={user}
                        editingSectionName={editingSectionName}
                        editingSectionValue={editingSectionValue}
                        onSectionSelect={handleSectionSelect}
                        onSectionNameChange={handleSectionNameChange}
                        onSaveSectionName={handleSaveSectionName}
                        onRenameSection={handleRenameSection}
                        onShareSection={section => {
                            setSelectedSectionForShare(section);
                            setIsShareModalOpen(true);
                        }}
                        onMakePrivateClick={handleMakePrivateClick}
                        onSectionDeleteClick={handleSectionDeleteClick}
                        onAddSection={handleAddSection}
                    />
                )}

                {showEntriesPane && (
                    <EntriesPane
                        selectedSection={selectedSection}
                        sortedNotes={sortedNotes}
                        editingNoteId={editingNote?.id}
                        sortOrder={sortOrder}
                        isSectionsCollapsed={isSectionsCollapsed}
                        projectUsersAll={projectUsersAll}
                        onToggleSectionsCollapsed={() => setIsSectionsCollapsed(prev => !prev)}
                        onToggleSort={() => setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))}
                        onAddEntry={handleAddEntry}
                        onSelectEntry={handleSelectEntry}
                    />
                )}

                {isAddingEntry ? (
                    <EditorChrome
                        sectionName={selectedSection?.name}
                        entryTitle={breadcrumbEntryTitle}
                        saveStatus={saveStatus}
                        isMaximized={isEditorMaximized}
                        onExportNote={handleExportNote}
                        onToggleMaximize={handleToggleMaximize}>
                        <JournalEditor
                            initialData={initialEditorData}
                            loadKey={editorLoadKey}
                            holder="editorjs"
                            onReadyApi={api => (editorApiRef.current = api)}
                            onChange={handleEditorChange}
                            handleEntryDeleteClick={handleEntryDeleteClick}
                            handleRewriteWithAI={mode => handleRewriteWithAI(mode, editingNote)}
                            handleMoveEntry={() => {
                                const currentNoteId = editingNote?.id || autoSavedNoteIdRef.current;
                                if (currentNoteId) {
                                    setIsMoveEntryModalOpen(true);
                                } else {
                                    toast.warning('Please save the note before moving it');
                                }
                            }}
                            isNewNote={false}
                            headerSlot={
                                <DocumentHeader
                                    entryTitle={entryTitle}
                                    ownerName={entryOwnerName}
                                    selectedSection={selectedSection}
                                    onTitleChange={handleTitleChange}
                                    onTitlePaste={handleTitlePaste}
                                    onMoveToBody={handleMoveToBody}
                                />
                            }
                            onBlur={async () => {
                                if (isAddingEntryRef.current && selectedSectionIdRef.current) {
                                    await flushPendingSave();
                                }
                            }}
                        />
                    </EditorChrome>
                ) : (
                    <div className={styles.editorPane}>
                        <div className={styles.editorEmptyState}>
                            {!selectedSection && 'Select a section'}
                            {selectedSection && (!selectedSection?.notes || selectedSection.notes.length === 0) && 'No notes in this section yet'}
                            {selectedSection && selectedSection?.notes?.length > 0 && 'Choose entry to display'}
                        </div>
                    </div>
                )}
            </div>

            <ShareSectionModal
                isOpen={isShareModalOpen}
                setIsShareModalOpen={setIsShareModalOpen}
                onOpenChange={(event, data) => setIsShareModalOpen(data.open)}
                sectionId={selectedSectionForShare?.id}
            />

            <DoubleConfirmationModal
                message={`Are you sure you want to delete the section "${selectedSectionForDelete?.name}"? This will also delete all entries in this section.`}
                handleAction={() => handleDeleteSection(selectedSectionForDelete?.id)}>
                <div ref={sectionMenuDeleteRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            <DoubleConfirmationModal
                type={'warn '}
                modalOneBtnL="Make Private"
                modalTwoBtnR="Make Private"
                message={`Make ${selectedSectionForMakePrivate?.name} Private?\n\nThis section is currently shared with your team. Making it private will remove their access.`}
                handleAction={() => selectedSectionForMakePrivate && handleMakePrivate(selectedSectionForMakePrivate)}>
                <div ref={sectionMakePrivateRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            <DoubleConfirmationModal
                message={`Are you sure you want to delete the entry "${selectedEntryForDelete?.title || 'Untitled'}"?`}
                handleAction={() => handleDeleteEntry(selectedEntryForDelete?.id)}>
                <div ref={entryMenuDeleteRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            <RewriteAIModal
                isOpen={isRewriteAIModalOpen}
                onOpenChange={e => {
                    setIsRewriteAIModalOpen(e.open);
                    if (!e.open) {
                        setRewrittenContentForSidebar(null);
                    }
                }}
                note={selectedNoteForRewrite}
                notepadId={selectedSectionId}
                setShowObsSidebar={rewrittenContent => {
                    if (rewrittenContent) {
                        setRewrittenContentForSidebar(rewrittenContent);
                    }
                    setShowObsSidebar(true);
                }}
                setShowTaskSidebar={rewrittenContent => {
                    if (rewrittenContent) {
                        setRewrittenContentForSidebar(rewrittenContent);
                    }
                    setShowTaskSidebar(true);
                }}
                setShowIRLSidebar={rewrittenContent => {
                    if (rewrittenContent) {
                        setRewrittenContentForSidebar(rewrittenContent);
                    }
                    setShowIRLSidebar(true);
                }}
                aiMode={aiMode}
                onReplace={newHtml => {
                    if (isAddingEntry) {
                        beginEditorLoad();
                        const { title: rawTitle, bodyHtml: rawBodyHtml } = splitNoteContent(newHtml || '');
                        const { title, bodyHtml } = applyTitleAndBody(rawTitle, rawBodyHtml);
                        setEntryTitle(title);
                        entryTitleRef.current = title;
                        setInitialEditorData(bodyHtml);
                        setDraftContent(bodyHtml);
                        latestDraftContentRef.current = bodyHtml;
                        originalNoteContentRef.current = newHtml || '';
                        setEditorLoadKey(k => k + 1);
                    }
                }}
            />

            <MoveEntryModal
                isOpen={isMoveEntryModalOpen}
                onOpenChange={e => setIsMoveEntryModalOpen(e.open)}
                noteId={editingNote?.id || autoSavedNoteIdRef.current}
                sourceSectionId={selectedSectionId}
                sections={sections}
                onMoveSuccess={targetSectionId => {
                    setIsAddingEntry(false);
                    resetEditorState();
                    setSelectedSectionId(targetSectionId);
                }}
            />

            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) {
                        setRewrittenContentForSidebar(null);
                    }
                }}
                defaultObservation={() => {
                    if (rewrittenContentForSidebar) {
                        return rewrittenContentForSidebar;
                    }
                    const currentNote = isAddingEntry && editingNote ? editingNote : selectedSection?.notes?.find(note => note.id === expandedNoteId);
                    return currentNote?.notes || '';
                }}
            />

            <TaskFormSidebar
                show={showTaskSidebar}
                setShow={show => {
                    setShowTaskSidebar(show);
                    if (!show) {
                        setRewrittenContentForSidebar(null);
                    }
                }}
                defaultTask={() => {
                    if (rewrittenContentForSidebar) {
                        return htmlToPlainText(rewrittenContentForSidebar);
                    }
                    const currentNote = isAddingEntry && editingNote ? editingNote : selectedSection?.notes?.find(note => note.id === expandedNoteId);
                    return htmlToPlainText(currentNote?.notes || '');
                }}
            />

            <PostInformationRequestSidebar
                show={showIRLSidebar}
                setShow={show => {
                    setShowIRLSidebar(show);
                    if (!show) {
                        setRewrittenContentForSidebar(null);
                    }
                }}
                defaultRequest={() => {
                    if (rewrittenContentForSidebar) {
                        return rewrittenContentForSidebar;
                    }
                    const currentNote = isAddingEntry && editingNote ? editingNote : selectedSection?.notes?.find(note => note.id === expandedNoteId);
                    return currentNote?.notes || '';
                }}
            />
        </div>
    );
};

export default Journal;
